<script>
  import { onMount } from 'svelte';
  import { show_message } from '../../lib/flash.ts';
  import BuyMachine from '../buy-machine.js';
  import ConfirmDialog from '../../lib/workpilots-ui/ConfirmDialog.svelte';
  import PaymentMethodSelector from '../../lib/workpilots-ui/PaymentMethodSelector.svelte';
  import NetsPaymentWindow from '../../lib/workpilots-ui/NetsPaymentWindow.svelte';
  import InputWithValidation from '../../lib/components/InputField.svelte';
  import AskForBilling from './AskForBilling.svelte';
  import { TS } from '../../config/translation-keys.js';
  import { t9n } from '../../lib/translate.js';
  import session from '../../lib/session';
  import { compareStrings } from '../../lib/utils';
  import Selector from '../../lib/components/Selector.svelte';
  import NumberFieldWithButtons from '../../lib/components/NumberFieldWithButtons.svelte';

  export let id;
  export let main;
  export let context;

  let machine = new BuyMachine({ id, main, context });
  let amount_pp = 0;
  let pp_price = 0;

  let option_cases = 'category';
  let is_ask_for_billing = false;
  let category_selector_items = [];
  let category_selector;
  let units;

  context = machine.context;

  $: {
    machine.update_context(context);
    context = machine.context;
  }

  const reset_state = () => {
    machine.reset_state();
    context = machine.context;
  };

  const finalize = async transaction_id => {
    let result = await machine.finalize_transaction(transaction_id);
    show_message(result.message, result.type);
    reset_state();
    context = machine.context;
  };

  const buy = async () => {
    await machine.buy();
    context = machine.context;
  };

  const set_payment_method = async e => {
    machine.set_payment_method(e);
    context = machine.context;
    await buy();
  };

  const validate = () => {
    machine.validate();
    context = machine.context;
  };

  const process_confirmed = () => {
    machine.process_confirmed();
    context = machine.context;
  };

  const show_buy_intent = () => {
    validate();
    if (context.valid) context.show_confirm = true;
    if (!context.show_confirm) process_confirmed();
  };

  const show_buy_intent_product = () => {
    validate();
    if (context.valid) context.show_confirm = true;
    if (!context.show_confirm) process_confirmed();
  };

  const get_name_of_product = localized_names => {
    for (let [key, value] of Object.entries(localized_names)) {
      if (key.includes($session.language)) {
        return value;
      }
    }
  };

  const get_categories = () => {
    let merchant = $session.merchant;

    for (let j = 0; j < merchant.products.length; j++) {
      let is_deleted = merchant.products[j].is_deleted;

      if (!is_deleted) {
        let no_payment = merchant.products[j].no_payment;
        let name = get_name_of_product(merchant.products[j].localized_names);
        let price = merchant.products[j].orderer_price.toFixed(2);
        let sign = merchant.currency_sign;
        let based_on = merchant.products[j].based_on;
        let new_item;

        // product_based_days
        // product_based_units
        // product_based_hours
        // product_based_months

        if (!no_payment) {
          let item_name;
          if (based_on === 'product_based_price') {
            item_name = `${name} (${sign})`;
          } else {
            if (based_on === 'product_based_days')
              item_name = `${name} (${t9n(
                TS.CREATE_ORDER_TASK_PRICE_DAY,
                `${price} ${sign}`
              )})`;
            if (based_on === 'product_based_units')
              item_name = `${name} (${t9n(
                TS.CREATE_ORDER_TASK_PRICE_UNIT,
                `${price} ${sign}`
              )})`;
            if (based_on === 'product_based_hours')
              item_name = `${name} (${t9n(
                TS.CREATE_ORDER_TASK_PRICE_HOUR,
                `${price} ${sign}`
              )})`;
            if (based_on === 'product_based_months')
              item_name = `${name} (${t9n(
                TS.CREATE_ORDER_TASK_PRICE_MONTH,
                `${price} ${sign}`
              )})`;
          }
          new_item = {
            name: item_name,
            raw_name: name,
            val: merchant.products[j].product_id,
            no_payment: no_payment,
          };
        } else {
          new_item = {
            name: `${name}`,
            raw_name: name,
            val: merchant.products[j].product_id,
            no_payment: no_payment,
          };
        }

        let tmp_name = new_item.name;
        let last_char_index = tmp_name.length - 1;
        if (tmp_name[last_char_index] === ' ') {
          new_item.name = tmp_name.slice(0, -1);
        }
        category_selector_items.push(new_item);
      }
    }

    category_selector_items.sort((a, b) => {
      return compareStrings(a.name, b.name);
    });
  };

  const calculate_pp = () => {
    if (!!category_selector && !!units) {
      let products = $session.merchant.products;

      let orderer_price;
      products.forEach(e => {
        if (e.product_id === category_selector) {
          orderer_price = e.orderer_price.toFixed(2);
        }
      });

      let amount = Math.floor(
        orderer_price * units * $session.merchant.pilot_points_multiplier
      );
      pp_price = Number(orderer_price * units).toFixed(2);

      context.buy_amount = amount;
      context.product_amount = units;
      context.product_orderer_price = orderer_price;
      machine.update_context(context);

      return amount;
    }

    return 0;
  };

  onMount(() => {
    get_categories();
    // value_field.focus(); todo: fix this!
  });

  $: if (category_selector && units) amount_pp = calculate_pp();
  // $: if (units) amount_pp = calculate_pp();
</script>

<div>
  <div style="display: flex; justify-content: center">
    <button
      class="mdc-button"
      class:mdc-button--outlined={option_cases === 'amount'}
      class:mdc-button--unelevated={option_cases === 'category'}
      style="margin-top: 16px"
      on:click|preventDefault|stopPropagation={() => {
        option_cases = 'category';
      }}>
      {t9n(TS.PILOT_POINTS_CATEGORY_AND_UNITS)}
    </button>

    <button
      class="mdc-button"
      class:mdc-button--outlined={option_cases === 'category'}
      class:mdc-button--unelevated={option_cases === 'amount'}
      style="margin-top: 16px"
      on:click|preventDefault|stopPropagation={() => {
        option_cases = 'amount';
      }}>
      {t9n(TS.PILOT_POINTS_ANY_AMOUNT)}
    </button>
  </div>

  {#if option_cases === 'amount'}
    <div class="content">

      <div class="fade-in">
        <InputWithValidation
          bind:value={context.buy_amount}
          on:input={validate}
          id="buy-description"
          type="text"
          label={context.buy_amount_label}
          name="buy_amount"
          validate={false}
          validation_text={context.error}
          valid={!context.buy_amount || context.valid}
          is_tooltip_default={true}
          tooltip_default_text="{context.explanation_1}
          {context.explanation_2}
          {context.explanation_3}"
          width="410px" />

        <span class="total">{context.total_text}</span>

        <br />

        <input
          id="buy_pp"
          type="submit"
          class="mdc-button mdc-button--unelevated margin-5"
          style="margin-top: 16px"
          value={context.processing ? 'Processing...' : context.buy_label}
          disabled={!context.buy_amount || !context.valid || context.processing}
          on:click|preventDefault={show_buy_intent} />

        <button
          id="btn_ask_billing"
          class="mdc-button mdc-button--unelevated"
          style="margin-top: 16px"
          disabled={!context.buy_amount || !context.valid || context.processing}
          on:click|preventDefault|stopPropagation={() => {
            is_ask_for_billing = !is_ask_for_billing;
          }}>
          {t9n(TS.PILOT_POINTS_ASK_FOR_BILLING)}
        </button>

        <img
          id="buy_pp_loader"
          src="/assets/spinners/orderer_loader.gif"
          alt="Loading"
          style="display: none" />
      </div>

      <div class="margin">
        <InputWithValidation
          id="buy-pp-amount"
          variant={'filled'}
          type="text"
          margin_bottom={'1px'}
          label={context.your_pp_label}
          name="pp-amount"
          bind:value={context.amount_text}
          width="100%" />
      </div>

    </div>
  {:else}
    <div class="content">

      <div class="fade-in">
        <Selector
          id={'selector-category'}
          label={t9n(TS.PILOT_POINTS_CHOOSE_CATEGORY)}
          variant={'outlined'}
          valid={true}
          showed_elements="4.5"
          invalid_text={t9n(TS.NEW_ORDER_DESC_TASK_TYPE)}
          bind:items={category_selector_items}
          is_required={true}
          width="410px"
          bind:value={category_selector} />

        <div style="margin-top: 20px">
          <NumberFieldWithButtons
            id="duration"
            type="text"
            label={t9n(TS.PILOT_POINTS_UNITS)}
            name="duration"
            bind:value={units}
            width="199px"
            amount={1}
            show_tooltip={false} />
        </div>

        <span class="total">
          {t9n(TS.PILOT_POINTS_BUY_BUY_AMOUNT_LABEL)}: {amount_pp}
        </span>
        <br />
        <span class="total">
          {t9n(TS.PILOT_POINTS_DONATE_TOTAL_TEXT, pp_price, $session.merchant.currency_sign)}
        </span>

        <br />

        <input
          id="buy_pp"
          type="submit"
          class="mdc-button mdc-button--unelevated margin-5"
          style="margin-top: 16px"
          value={context.processing ? 'Processing...' : context.buy_label}
          disabled={amount_pp === 0}
          on:click|preventDefault={show_buy_intent_product} />

        <button
          id="btn_ask_billing"
          class="mdc-button mdc-button--unelevated"
          style="margin-top: 16px"
          disabled={amount_pp === 0}
          on:click|preventDefault|stopPropagation={() => {
            is_ask_for_billing = !is_ask_for_billing;
          }}>
          {t9n(TS.PILOT_POINTS_ASK_FOR_BILLING)}
        </button>

        <img
          id="buy_pp_loader"
          src="/assets/spinners/orderer_loader.gif"
          alt="Loading"
          style="display: none" />
      </div>

      <div class="margin">
        <InputWithValidation
          id="buy-pp-amount"
          variant={'filled'}
          type="text"
          margin_bottom={'1px'}
          label={context.your_pp_label}
          name="pp-amount"
          bind:value={context.amount_text}
          width="100%" />
      </div>

    </div>
  {/if}

  {#if is_ask_for_billing}
    <AskForBilling
      amount={context.buy_amount}
      product_amount={context.product_amount}
      product_orderer_price={context.product_orderer_price} />
  {/if}

  <ConfirmDialog
    bind:show_confirm_flag={context.show_confirm}
    bind:confirmed_flag={context.confirmed}
    confirm_title={context.confirm_title}
    confirm_text={context.confirm_text}
    confirm_no_label={context.confirm_no_label}
    confirm_yes_label={context.confirm_yes_label}
    confirm_yes_action={process_confirmed} />

  <PaymentMethodSelector
    amount={context.buy_amount}
    options={context.payment_methods}
    showpp={false}
    bind:displayflag={context.show_payment_choices}
    on:paymentmethodselected={async e => {
      await set_payment_method(e);
    }} />

  <NetsPaymentWindow
    nets_host={context.nets_host}
    show={context.show_payment_window}
    transaction_id={context.transaction_id}
    callback={finalize} />

  <div class="billing-dialog" />

</div>

<style>
  #buy_pp:disabled {
    color: darkgrey;
  }

  #buy_pp:focus,
  #buy_pp:hover {
    outline: 0;
  }

  .total {
    color: dimgray;
    font-size: 90%;
    text-align: left;
    margin-top: -16px;
  }

  .content {
    margin-bottom: 50px;
    margin-top: 60px;
    /*margin-left: 25px;*/
    justify-content: center;
    display: flex;
  }

  .margin {
    margin-left: 10px;
  }

  .hidden {
    display: none;
  }
</style>
